import { Link, Outlet } from "react-router-dom";
import ErrorBoundary from "./ErrorBoundary";
import Logo from "./Logo";
import UserMenu from "./UserMenu";

// Build info injected at build time
declare const __BUILD_COMMIT__: string;
declare const __BUILD_COMMIT_SHORT__: string;
declare const __BUILD_DATE__: string;
declare const __BUILD_MESSAGE__: string;

function getRelativeTime(dateString: string): string {
  if (!dateString) return "";
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();

  // Handle future dates (likely clock skew) - treat as just deployed
  if (diffMs < 0) return "Deployed just now";

  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor(diffMs / (1000 * 60));

  if (diffDays > 0) return `Deployed ${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
  if (diffHours > 0) return `Deployed ${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
  if (diffMinutes > 0) return `Deployed ${diffMinutes} minute${diffMinutes === 1 ? "" : "s"} ago`;
  return "Deployed just now";
}

export default function Layout() {
  const commitHash = typeof __BUILD_COMMIT_SHORT__ !== "undefined" ? __BUILD_COMMIT_SHORT__ : "dev";
  const buildDate = typeof __BUILD_DATE__ !== "undefined" ? __BUILD_DATE__ : "";
  const commitMessage = typeof __BUILD_MESSAGE__ !== "undefined" ? __BUILD_MESSAGE__ : "";
  const relativeTime = getRelativeTime(buildDate);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg flex flex-col">
      {/* Header */}
      <header className="bg-primary-500 dark:bg-dark-bg-secondary dark:border-b dark:border-dark-border text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <Logo className="w-12 h-5 text-white" />
              <span className="font-semibold text-lg">Shopify Dev OAuth</span>
            </Link>
            <UserMenu />
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </main>

      {/* Footer */}
      <footer className="bg-gray-100 dark:bg-dark-bg-secondary border-t border-gray-200 dark:border-dark-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-center gap-2 text-xs text-gray-400 dark:text-dark-text-muted">
            {relativeTime && <span>{relativeTime}</span>}
            {relativeTime && commitHash !== "dev" && (
              <span className="text-gray-300 dark:text-dark-border">|</span>
            )}
            {commitHash !== "dev" && <span className="font-mono">{commitHash}</span>}
            {commitMessage && <span className="text-gray-300 dark:text-dark-border">|</span>}
            {commitMessage && <span className="truncate max-w-md">{commitMessage}</span>}
            {commitHash === "dev" && !relativeTime && <span>Development Mode</span>}
          </div>
        </div>
      </footer>
    </div>
  );
}
